import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Menu,
  Search,
  Bell,
  Sun,
  Moon,
  User,
  Settings,
  LogOut,
  ChevronDown,
  Shield,
} from 'lucide-react';

import { ROUTES } from '@/utils/constants';
import { useAuth } from '@/hooks/useAuth';
import { useTheme } from '@/context/ThemeContext';
import { cn } from '@/utils/cn';
import { useAppDispatch, useAppSelector } from '@/store';
import {
  fetchNotifications,
  fetchUnreadCount,
  selectUnreadNotifications,
  selectUnreadCount,
  optimisticMarkAsRead,
  markNotificationAsRead,
} from '@/store/notificationSlice';
import { formatDistanceToNow } from '@/utils/formatters';

interface HeaderProps {
  onMenuClick: () => void;
}

function Header({ onMenuClick }: HeaderProps): React.ReactElement {
  const { user, handleLogout } = useAuth();
  const { effectiveTheme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const notificationRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Get notifications from Redux
  const notifications = useAppSelector(selectUnreadNotifications);
  const unreadCount = useAppSelector(selectUnreadCount);

  // Fetch notifications on mount and poll periodically
  useEffect(() => {
    dispatch(fetchNotifications({ read: false, limit: 5 }));
    dispatch(fetchUnreadCount());

    // Poll every 30 seconds for new notifications
    const interval = setInterval(() => {
      dispatch(fetchUnreadCount());
    }, 30000);

    return () => clearInterval(interval);
  }, [dispatch]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setIsNotificationsOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close all dropdowns on route change to prevent blur artifacts
  useEffect(() => {
    setIsSearchOpen(false);
    setIsUserMenuOpen(false);
    setIsNotificationsOpen(false);
  }, [location.pathname]);

  const handleNotificationClick = (notificationId: string, actionUrl: string | null) => {
    // Mark as read
    dispatch(optimisticMarkAsRead(notificationId));
    dispatch(markNotificationAsRead(notificationId));

    // Close dropdown
    setIsNotificationsOpen(false);

    // Navigate if action URL exists
    if (actionUrl) {
      navigate(actionUrl);
    }
  };

  return (
    <header
      className="sticky top-0 z-30 h-14 sm:h-16 bg-background-card/95 backdrop-blur-md border-b border-border safe-area-inset-top"
      style={{ WebkitBackfaceVisibility: 'hidden', backfaceVisibility: 'hidden', transform: 'translateZ(0)' }}
    >
      <div className="flex items-center justify-between h-full px-3 sm:px-4">
        {/* Left section */}
        <div className="flex items-center gap-2 sm:gap-4">
          {/* Mobile menu button - larger touch target */}
          <button
            onClick={onMenuClick}
            className="p-2.5 sm:p-2 -ml-1 sm:ml-0 rounded-lg hover:bg-background-elevated active:bg-background-muted transition-colors lg:hidden touch-manipulation"
            aria-label="Toggle menu"
          >
            <Menu className="h-6 w-6 sm:h-5 sm:w-5 text-foreground-muted" />
          </button>

          {/* Mobile logo - show on small screens */}
          <Link to={ROUTES.DASHBOARD} className="lg:hidden">
            <h1 className="text-lg font-bold text-gradient-primary">BOIS</h1>
          </Link>

          {/* Search - hidden on mobile, compact on tablet */}
          <div className="relative hidden sm:block">
            <button
              onClick={() => setIsSearchOpen(!isSearchOpen)}
              className="flex items-center gap-2 px-3 py-1.5 sm:py-2 rounded-lg bg-background-elevated hover:bg-background-muted transition-colors"
            >
              <Search className="h-4 w-4 text-foreground-muted" />
              <span className="text-sm text-foreground-subtle hidden md:inline">
                Search...
              </span>
              <kbd className="hidden lg:inline-flex items-center gap-1 px-1.5 py-0.5 text-xs text-foreground-muted bg-background rounded border border-border">
                <span className="text-xs">Ctrl</span>K
              </kbd>
            </button>

            {/* Search overlay */}
            <AnimatePresence>
              {isSearchOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute top-full left-0 mt-2 w-80 sm:w-96 p-4 bg-background-card rounded-lg border border-border shadow-lg"
                >
                  <input
                    type="text"
                    placeholder="Search..."
                    className="w-full px-3 py-2 bg-background-elevated rounded-lg border border-border text-foreground placeholder:text-foreground-subtle focus:outline-none focus:ring-2 focus:ring-primary"
                    autoFocus
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Right section */}
        <div className="flex items-center gap-1 sm:gap-2">
          {/* Mobile search button */}
          <button
            onClick={() => setIsSearchOpen(!isSearchOpen)}
            className="sm:hidden p-2.5 rounded-lg hover:bg-background-elevated active:bg-background-muted transition-colors touch-manipulation"
            aria-label="Search"
          >
            <Search className="h-5 w-5 text-foreground-muted" />
          </button>

          {/* Theme toggle - larger touch target on mobile */}
          <button
            onClick={toggleTheme}
            className="p-2.5 sm:p-2 rounded-lg hover:bg-background-elevated active:bg-background-muted transition-colors touch-manipulation"
            aria-label={effectiveTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {effectiveTheme === 'dark' ? (
              <Sun className="h-5 w-5 text-foreground-muted" />
            ) : (
              <Moon className="h-5 w-5 text-foreground-muted" />
            )}
          </button>

          {/* Notifications - larger touch target on mobile */}
          <div className="relative" ref={notificationRef}>
            <button
              onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
              className="relative p-2.5 sm:p-2 rounded-lg hover:bg-background-elevated active:bg-background-muted transition-colors touch-manipulation"
              aria-label="Notifications"
            >
              <Bell className="h-5 w-5 text-foreground-muted" />
              {unreadCount > 0 && (
                <span className="absolute top-1.5 right-1.5 sm:top-1 sm:right-1 min-w-[18px] h-[18px] px-1 text-[11px] font-medium bg-destructive text-white rounded-full flex items-center justify-center">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            {/* Notifications dropdown - mobile optimized */}
            <AnimatePresence>
              {isNotificationsOpen && (
                <>
                  <div
                    className="fixed inset-0 z-40 bg-transparent"
                    onClick={() => setIsNotificationsOpen(false)}
                    onPointerDown={() => setIsNotificationsOpen(false)}
                  />
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                    className="absolute right-0 mt-2 w-[calc(100vw-2rem)] sm:w-80 max-w-sm bg-background-card rounded-lg border border-border shadow-lg z-50"
                  >
                    <div className="p-4 border-b border-border">
                      <h3 className="font-medium text-foreground">Notifications</h3>
                    </div>
                    <div className="max-h-64 sm:max-h-80 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <div className="px-4 py-8 text-center text-foreground-muted text-sm">
                          No new notifications
                        </div>
                      ) : (
                        notifications.slice(0, 5).map((notification) => (
                          <div
                            key={notification.id}
                            onClick={() => handleNotificationClick(notification.id, notification.actionUrl)}
                            className={cn(
                              'px-4 py-3 hover:bg-background-elevated active:bg-background-muted cursor-pointer transition-colors touch-manipulation',
                              !notification.read && 'bg-primary/5'
                            )}
                          >
                            <div className="flex items-start gap-3">
                              {!notification.read && (
                                <div className="w-2 h-2 mt-1.5 rounded-full bg-primary flex-shrink-0" />
                              )}
                              <div className={notification.read ? 'ml-5' : ''}>
                                <p className="text-sm text-foreground">
                                  {notification.title}
                                </p>
                                {notification.description && (
                                  <p className="text-xs text-foreground-muted mt-0.5">
                                    {notification.description}
                                  </p>
                                )}
                                <p className="text-xs text-foreground-subtle mt-1">
                                  {formatDistanceToNow(notification.createdAt)}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                    <div className="p-2 border-t border-border">
                      <Link
                        to={ROUTES.NOTIFICATIONS}
                        className="block w-full px-3 py-2 text-sm text-center text-primary hover:bg-background-elevated active:bg-background-muted rounded-lg transition-colors touch-manipulation"
                        onClick={() => setIsNotificationsOpen(false)}
                      >
                        View all notifications
                      </Link>
                    </div>
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>

          {/* Admin button - hide label on mobile */}
          {user?.role === 'admin' && (
            <Link
              to={ROUTES.ADMIN}
              className="p-2.5 sm:p-2 rounded-lg hover:bg-background-elevated active:bg-background-muted transition-colors touch-manipulation"
              title="Admin Dashboard"
            >
              <Shield className="h-5 w-5 text-foreground-muted" />
            </Link>
          )}

          {/* User menu - mobile optimized */}
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              className="flex items-center gap-2 p-1 sm:p-1.5 rounded-lg hover:bg-background-elevated active:bg-background-muted transition-colors touch-manipulation"
            >
              <div className="w-8 h-8 sm:w-8 sm:h-8 rounded-full bg-gradient-primary flex items-center justify-center">
                <span className="text-sm font-medium text-background">
                  {user?.profile?.displayName?.charAt(0) ?? user?.profile?.username?.charAt(0) ?? 'U'}
                </span>
              </div>
              <ChevronDown
                className={cn(
                  'h-4 w-4 text-foreground-muted transition-transform hidden sm:block',
                  isUserMenuOpen && 'rotate-180'
                )}
              />
            </button>

            {/* User dropdown - mobile optimized */}
            <AnimatePresence>
              {isUserMenuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-40 bg-transparent"
                    onClick={() => setIsUserMenuOpen(false)}
                    onTouchEnd={() => setIsUserMenuOpen(false)}
                  />
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                    className="absolute right-0 mt-2 w-56 bg-background-card rounded-lg border border-border shadow-lg z-50"
                  >
                    <div className="p-4 border-b border-border">
                      <p className="font-medium text-foreground truncate">
                        {user?.profile?.displayName ?? user?.profile?.username ?? 'User'}
                      </p>
                      <p className="text-sm text-foreground-subtle truncate">
                        {user?.email ?? ''}
                      </p>
                    </div>
                    <div className="p-2">
                      <Link
                        to={ROUTES.PROFILE}
                        className="flex items-center gap-3 px-3 py-2.5 sm:py-2 text-sm text-foreground-muted hover:text-foreground hover:bg-background-elevated active:bg-background-muted rounded-lg transition-colors touch-manipulation"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <User className="h-4 w-4" />
                        Profile
                      </Link>
                      <Link
                        to={ROUTES.SETTINGS}
                        className="flex items-center gap-3 px-3 py-2.5 sm:py-2 text-sm text-foreground-muted hover:text-foreground hover:bg-background-elevated active:bg-background-muted rounded-lg transition-colors touch-manipulation"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <Settings className="h-4 w-4" />
                        Settings
                      </Link>
                    </div>
                    <div className="p-2 border-t border-border">
                      <button
                        onClick={() => {
                          setIsUserMenuOpen(false);
                          handleLogout();
                        }}
                        className="flex items-center gap-3 w-full px-3 py-2.5 sm:py-2 text-sm text-destructive hover:bg-destructive/10 active:bg-destructive/20 rounded-lg transition-colors touch-manipulation"
                      >
                        <LogOut className="h-4 w-4" />
                        Logout
                      </button>
                    </div>
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </header>
  );
}

export default Header;
